import React from 'react'; // Ensure this import is present
import { P2Alert, P2Button, P2Checkbox, P2TextDescription, P2TextInput, P2TextLabel } from 'react-bootstrap-common-script';
import ApplicationCooLayout from '../../../layout/application-coo-layout';
import { Separator } from '../../../components/separator';
import { useNavigate } from 'react-router-dom';
import { SectionCard } from '../../../components/card';
import { Stepper } from '../../../components/stepper';
import { R2stepperItemsStageE } from '../../../utils/shared-dropdown';
import { InfoCircle } from '../../../assets/svg';
import RadioGroup from '../../../components/form/grouped/radio/radio-group';
import { CPFWebsite, HPSTermsAndCondition } from '../stage-e-link-util';
import { useAppDispatch, useAppSelector } from '../../../redux/hook';
import { resetFormData, resetStep3FormData, updateFormData, updateStep3FormData } from '../../../redux/slices/application-stage-e-slice';
import { useEffect, useState } from 'react';
import ShareCover from './segments/share-cover';
import { HealthDeclarationItems } from '../../../utils/shared-radio';
import { useGetApplicationHdbRefQuery, useProposedHpsCoverageQuery } from '../../../redux/services/getStageE';
import useViewportWidth from '../../../components/stepper/useViewportWidth';
import { LottieLoader } from '../../../components/loader';
const ACTIVE_STEP = 3;

interface AlertStepProps {
  variant: string;
  message: React.ReactNode;
  onBack: () => void;
  onNext: () => void;
  nextDisabled: boolean;
}

// Reusable wrapper for non-form alerts
const AlertStep = ({ variant, message, onBack, onNext, nextDisabled }: AlertStepProps) => (
  <ApplicationCooLayout title='Application for Use of CPF Savings and HPS for Change in Flat Ownership'>
    <div className='polymer-application-coo__wrapper cpf-wrapper'>
      <Stepper activeStep={ACTIVE_STEP} items={R2stepperItemsStageE} />
      <div className='d-flex justify-content-center'>
        <SectionCard
          required
          className='flex-fill'
          activeStep={ACTIVE_STEP}
          stepperItems={R2stepperItemsStageE}
          titleChildren={<P2TextDescription title={R2stepperItemsStageE[ACTIVE_STEP - 1].title} />}
          contentChildren={
            <div className='polymer-stage-e cpf-container' style={{ fontSize: 18, lineHeight: '24px' }}>
              <P2Alert variant={variant} withBorder text={message} />
            </div>
          }
        />
      </div>
      <FooterButtons onBack={onBack} onNext={onNext} nextDisabled={nextDisabled} />
    </div>
  </ApplicationCooLayout>
);

interface FooterButtonsProps {
  onBack: () => void;
  onNext: () => void;
  nextDisabled: boolean;
}

const FooterButtons = ({ onBack, onNext, nextDisabled }: FooterButtonsProps) => (
  <div className='cpf-wrapper_button-wrapper'>
    <div className='cpf-wrapper_button-group'>
      <P2Button onClick={onBack} variant='outline-primary'>
        Back
      </P2Button>
      <P2Button onClick={onNext} disabled={nextDisabled} variant='primary-1'>
        Continue
      </P2Button>
    </div>
  </div>
);

interface SummarySectionProps {
  loginUser: any;
  formData: any;
  selectedSN: string[];
  proposedHdbRef: string;
}

const SummarySection: React.FC<SummarySectionProps> = ({ loginUser, formData, selectedSN, proposedHdbRef }) => (
  <>
    <div>
      <P2TextLabel bold={700} label='Summary' fontSize='24px' lineHeight='28px' />
    </div>
    <div className='address-container'>
      {[
        { label: 'CPF Account No.', value: loginUser?.uinfin },
        { label: 'Name', value: loginUser?.name },
        { label: 'HDB Reference No.', value: proposedHdbRef },
        { label: 'CPF Withdrawal Form S/N', value: selectedSN.join(' AND ') },
      ].map(({ label, value }) => (
        <div key={label} className='address-box'>
          <div className='title'>
            <P2TextLabel label={label} fontSize='18px' lineHeight='24px' />
          </div>
          <div className='content'>
            <p>{value}</p>
          </div>
        </div>
      ))}
    </div>
  </>
);

function StageEHomeProtectionScheme() {
  const isDesktopView = useViewportWidth();
  const navigate = useNavigate();
  const appDispatch = useAppDispatch();

  // Extract query parameters
  const queryParameters = new URLSearchParams(window.location.search);
  const applicationId = queryParameters.get('application_id') ?? '0';

  // Redux selectors
  const startApplicationFormData = useAppSelector((state) => state.app.ApplicationStartSlice.formData);

  // Redux state
  const { loginUser, singpassToken } = useAppSelector((s) => s.auth.AuthenticationSlice);
  const { formData: healthDeclarationFormData, stage_e_selected_form_sn: selectedCpfFormSN } = useAppSelector((s) => s.app.ApplicationStageESlice);

  const { data: applicationHdbRefData } = useGetApplicationHdbRefQuery(
    {
      application_id: applicationId,
      uinfin: loginUser?.uinfin ?? '',
      singpass_token: singpassToken ?? '',
    },
    { skip: applicationId === '0' || applicationId === null }
  );

  // API calls
  const { data: proposedHpsCoverage, isLoading: isProposedHpsCoverageLoading } = useProposedHpsCoverageQuery({
    application_id: applicationId,
    uinfin: loginUser?.uinfin ?? '',
    singpass_token: singpassToken ?? '',
  });

  // Local state
  const [agree1, setAgree1] = useState(false);
  const [agree2, setAgree2] = useState(false);

  // Reset on mount
  useEffect(() => {
    appDispatch(resetFormData());
    appDispatch(resetStep3FormData());
  }, [appDispatch]);

  // Navigate handlers
  const handleNavigateBack = () => {
    navigate(`/application-coo/stage-e/request-use-of-cpf?application_id=${applicationId}`);
  };

  const handleNavigateNext = () => {
    appDispatch(
      updateStep3FormData({
        hpsCoverageForLoginUser: coverage,
        hpsRequired: applied,
        isLoginUserZeroCoverage: zeroCoverage,
      })
    );
    navigate(`/application-coo/stage-e/confirmation-declaration?application_id=${applicationId}`);
  };

  // Derived variables
  const isPartBCompleted =
    healthDeclarationFormData.radio_1 !== '' && healthDeclarationFormData.radio_2 !== '' && healthDeclarationFormData.radio_3 !== '';
  const applied = proposedHpsCoverage?.applied || false;
  const coverage = proposedHpsCoverage?.hps_coverage || 0;
  const zeroCoverage = coverage === 0;

  if (isProposedHpsCoverageLoading) {
    return <LottieLoader />;
  }

  if (!applied) {
    return (
      <AlertStep
        variant='warning'
        message='You are not required to be insured under HPS, as you are not taking any new HDB loan to finance the change in flat ownership.'
        onBack={handleNavigateBack}
        onNext={handleNavigateNext}
        nextDisabled={false}
      />
    );
  }

  if (zeroCoverage) {
    return (
      <AlertStep
        variant='warning'
        message={
          <>
            You are encouraged to be insured under HPS, even if you are using cash for your monthly housing instalments.
            <br /> <br />
            If you have an existing HPS cover, but have reviewed that you do not need the financial protection provided by HPS, you may terminate your
            existing HPS cover through CPF Board's{' '}
            <a href='https://www.cpf.gov.sg/hps45a' target='_blank' rel='noreferrer' className='polymer-text-links'>
              website
            </a>
          </>
        }
        onBack={handleNavigateBack}
        onNext={handleNavigateNext}
        nextDisabled={false}
      />
    );
  }

  return (
    <ApplicationCooLayout title='Apply to Withdraw CPF Funds Due to Change in Flat Ownership'>
      <div className='polymer-application-coo__wrapper cpf-wrapper'>
        <div>
          <Stepper activeStep={ACTIVE_STEP} items={R2stepperItemsStageE} />
        </div>

        <div className='d-flex flex-column align-items-center gap-3 gap-md-5'>
          <SectionCard
            required={true}
            className='flex-fill'
            activeStep={ACTIVE_STEP}
            stepperItems={R2stepperItemsStageE}
            titleChildren={<P2TextDescription title={R2stepperItemsStageE[ACTIVE_STEP - 1]?.title} />}
            contentChildren={
              <div className='polymer-stage-e'>
                <div className='cpf-container'>
                  {/* SUMMARY */}
                  <SummarySection
                    loginUser={loginUser}
                    formData={startApplicationFormData}
                    selectedSN={selectedCpfFormSN}
                    proposedHdbRef={applicationHdbRefData?.proposed_hdb_ref ?? ''}
                  />

                  <div className='polymer-stage-e_step-3_summary-info'>
                    <div className='info-svg'>
                      <InfoCircle />
                    </div>
                    <div className='info-list'>
                      <div className='info-title'>Note:</div>
                      <ol>
                        <li>
                          You are required to be insured under the Home Protect Scheme (HPS), if you:
                          <ol type='a'>
                            <li> have applied to take over an HDB property</li>
                            <li> have applied to use your CPF savings to pay the monthly housing loan instalments of your HDB flat, and </li>
                            <li> are in good health </li>
                          </ol>
                        </li>

                        <li>
                          The CPF Board (CPFB) will issue an HPS cover to you when you have:
                          <ol type='a'>
                            <li>Obtained legal ownership of the flat</li>
                            <li>Executed the loan document with HDB, and are legally liable for the loan</li>
                            <li>Made a health declaration, and are accepted for cover under HPS</li>
                            <li>Paid your HPS premium*</li>
                          </ol>
                        </li>

                        <li>
                          Please read through the <HPSTermsAndCondition /> before proceeding.
                        </li>
                        <li>
                          You may visit the <CPFWebsite /> for more information about HPS.
                        </li>
                      </ol>

                      <span className='info-clause'>
                        *The applicable annual premium will be deducted from your CPF Ordinary Account. You will be informed on the top-up options if
                        there are not enough funds in your account.
                      </span>
                    </div>
                  </div>
                </div>
                <Separator variant='solid' style={{ margin: '2.5rem 0 1.5rem 0' }} />

                {/* PART - A */}
                <div className='cpf-container'>
                  <div>
                    <P2TextLabel bold={700} label={'Part A - Declaration of Share of Cover'} fontSize={'24px'} lineHeight={'28px'} />
                  </div>
                  <ShareCover hpsCoverage={coverage} />
                  <div>
                    <P2TextDescription
                      color='#333'
                      description_margin='0'
                      description={
                        'The total share of coverage of all owners must add up to at least 100% of the outstanding housing loan. You are encouraged to apply for a share of cover that corresponds to your share of responsibility in servicing the loan.'
                      }
                    />
                  </div>
                </div>
                <Separator variant='solid' style={{ margin: '0 0 1.5rem 0' }} />

                {/* PART - B */}
                <div className='cpf-container'>
                  <div>
                    <P2TextLabel bold={700} label={'Part B - Health Declaration'} fontSize={'24px'} lineHeight={'28px'} />
                  </div>
                  <div className='polymer-stage-e_step-3_summary-info'>
                    <div className='info-svg'>
                      <InfoCircle />
                    </div>
                    <div className='info-list'>
                      <div className='info-title'>Note:</div>
                      <p>
                        You must disclose all information which you know or ought to know, that may affect your cover under HPS, so it is compulsory
                        to answer all questions below. You must inform the CPF Board immediately if there is a change in the state of your health
                        before this policy is issued to you.
                      </p>
                    </div>
                  </div>

                  <div className='polymer-stage-e_part-b'>
                    <div className='polymer-stage-e_part-b_medical-option'>
                      <div>
                        <P2TextLabel bold={700} label={'1.'} fontSize={'18px'} lineHeight={'28px'} />
                      </div>
                      <div className='polymer-stage-e_part-b_medical-option_q'>
                        <RadioGroup
                          required={true}
                          r2Enabled={true}
                          label='Have you ever suffered or been treated for, or do you now suffer from medical condition such as cancer, tumour, high cholesterol, high blood pressure, diabetes, heart disorder, stroke, asthma, fits, depression, hepatitis, HIV infection, drug or alcohol addictions, disease or disorder of the brain, bone, muscle, spine, kidney, blood, liver, thyroid, or lung, or any other ailment, disorder, physical or mental impairment not mentioned here?'
                          layout='default'
                          gap={8}
                          items={HealthDeclarationItems}
                          value={healthDeclarationFormData.radio_1}
                          onItemSelect={(e: string) => appDispatch(updateFormData({ ...healthDeclarationFormData, radio_1: e }))}
                        />
                        <div className='polymer-stage-e_part-b_medical-option_input'>
                          <P2TextInput
                            required={false}
                            value={healthDeclarationFormData.input_1}
                            disabled={healthDeclarationFormData.radio_1 === 'Y' ? false : true}
                            name='name'
                            label='If Yes, please specify'
                            placeholder='Please specify your medical conditions.'
                            type='text'
                            onChange={(e: string) => appDispatch(updateFormData({ ...healthDeclarationFormData, input_1: e }))}
                          />
                        </div>
                      </div>
                    </div>

                    <div className='polymer-stage-e_part-b_medical-option'>
                      <div>
                        <P2TextLabel bold={700} label={'2.'} fontSize={'18px'} lineHeight={'28px'} />
                      </div>
                      <div className='polymer-stage-e_part-b_medical-option_q'>
                        <RadioGroup
                          required={true}
                          r2Enabled={true}
                          label='Have you undergone any surgery, treatment, or had any medical test or investigation carried out in the past 5 years?'
                          layout='default'
                          gap={8}
                          items={HealthDeclarationItems}
                          value={healthDeclarationFormData.radio_2}
                          onItemSelect={(e: string) => appDispatch(updateFormData({ ...healthDeclarationFormData, radio_2: e }))}
                        />
                        <div className='polymer-stage-e_part-b_medical-option_input'>
                          <P2TextInput
                            required={false}
                            value={healthDeclarationFormData.input_2}
                            disabled={healthDeclarationFormData.radio_2 === 'Y' ? false : true}
                            name='name'
                            label='If Yes, please specify'
                            placeholder='Please specify your medical conditions.'
                            type='text'
                            onChange={(e: string) => appDispatch(updateFormData({ ...healthDeclarationFormData, input_2: e }))}
                          />
                        </div>
                      </div>
                    </div>

                    <div className='polymer-stage-e_part-b_medical-option'>
                      <div>
                        <P2TextLabel bold={700} label={'3.'} fontSize={'18px'} lineHeight={'28px'} />
                      </div>
                      <div className='polymer-stage-e_part-b_medical-option_q'>
                        <RadioGroup
                          required={true}
                          r2Enabled={true}
                          layout='default'
                          gap={8}
                          label='Are you intending to go for any medical consultation, surgery, treatment, medical test or investigation in the next 1 year?'
                          items={HealthDeclarationItems}
                          value={healthDeclarationFormData.radio_3}
                          onItemSelect={(e: string) => appDispatch(updateFormData({ ...healthDeclarationFormData, radio_3: e }))}
                        />
                        <div className='polymer-stage-e_part-b_medical-option_input'>
                          <P2TextInput
                            required={false}
                            value={healthDeclarationFormData.input_3}
                            disabled={healthDeclarationFormData.radio_3 === 'Y' ? false : true}
                            name='name'
                            label='If Yes, please specify'
                            placeholder='Please specify your medical conditions.'
                            type='text'
                            onChange={(e: string) => appDispatch(updateFormData({ ...healthDeclarationFormData, input_3: e }))}
                          />
                        </div>
                      </div>
                    </div>
                    <p className='polymer-stage-e_18pt_description'>
                      Please be informed that you may be required to undergo a medical examination and the CPF Board may request a copy of the medical
                      report on your health condition.
                    </p>
                  </div>
                </div>
                <Separator variant='solid' style={{ margin: '1.5rem 0 1.5rem 0' }} />

                {/* PART - C */}
                <div className='cpf-container part-c'>
                  <div>
                    <P2TextLabel bold={700} label={'Part C - Authorisation & Declaration'} fontSize={'24px'} lineHeight={'28px'} />
                  </div>
                  {/* Declaration  */}
                  <div className='polymer-stage-e_checkbox-wrapper' onClick={() => setAgree1((prev) => !prev)} style={{ cursor: 'pointer' }}>
                    <P2Checkbox variant={'default'} isActive={agree1} />
                    <div>
                      <P2TextLabel label={'I declare that:'} fontSize={'18px'} lineHeight={'24px'} />
                      <ol className='polymer-stage-e_ordered-list-wrapper'>
                        <li>
                          to my best knowledge and belief, the information given in this form is true and complete and that I have not withheld any
                          facts or information which may affect my cover under HPS. I understand that any false or incomplete information may render
                          my HPS cover invalid and the claim benefits will not be admissible and there will be no refund of premium.
                        </li>
                        <br />
                        <li>
                          I understand that I should declare a share of cover that matches the proportion I am paying for my monthly housing
                          instalments, including CPF savings and/or cash.
                        </li>
                        <br />
                        <li>
                          I have read and agree to the <HPSTermsAndCondition />.
                        </li>
                      </ol>
                    </div>
                  </div>

                  {/* Agree and Authorise */}
                  <div className='polymer-stage-e_checkbox-wrapper' onClick={() => setAgree2((prev) => !prev)} style={{ cursor: 'pointer' }}>
                    <P2Checkbox variant={'default'} isActive={agree2} />
                    <div>
                      <P2TextLabel label={'I agree and authorise:'} fontSize={'18px'} lineHeight={'24px'} />
                      <ol className='polymer-stage-e_ordered-list-wrapper'>
                        <li>
                          that for the purpose of the CPF Board's performance of its duties under HPS,
                          <ol className='polymer-stage-e_ordered-list-wrapper_innerlist' type='a'>
                            <li>
                              any person or organisation, including any medical practitioner, medical institution, insurance company, and mortgagee
                              (collectively, "third parties"), to release to the CPF Board any information concerning me as the CPF Board may consider
                              appropriate;
                            </li>
                            <li>the CPFB to disclose to any third parties, such information that the CPF Board may consider appropriate; and</li>
                            <li>
                              the CPF Board to use any information obtained by the CPF Board in the course of performing the CPF Board's functions or
                              duties under the CPF Act (Cap. 36) and the MediShield Life Scheme Act 2015 ("MSL Act")
                            </li>
                          </ol>
                        </li>
                        <br />
                        <li>
                          the CPF Board and any insurers offering insurance cover under the CPF Act or MSL Act to obtain and use the information in
                          and obtained under this application for any purpose connected with the administration or operation of my accounts maintained
                          by the CPF Board, or for the administration of any existing and future schemes under the CPF Act or MSL Act.
                        </li>
                        <br />
                        <li>
                          the CPF Board and any third parties shall not be liable for any loss, damage, cost and expense which I may suffer arising
                          from disclosure or usage of my information pursuant to the authorisation herein. This authorisation shall remain valid after
                          my death.
                        </li>
                      </ol>
                      <br />
                      <p>
                        I further agree that the CPF Board may, by notice, change these terms and conditions from time to time by posting the new
                        terms and conditions on the <CPFWebsite />. Unless indicated otherwise, the changes will apply to you and will be effective as
                        of the date the changes are posted on the <CPFWebsite />. You are advised to check the <CPFWebsite /> regularly. The CPF Board
                        may also choose to inform you by other means of communication. Your continued participation in the scheme or the use of the
                        CPF Board's services constitutes acceptance of the new terms and conditions.
                      </p>
                      <br />
                      <p>By submitting this page, I declare that I have read and accepted the declaration above.</p>
                    </div>
                  </div>
                </div>
              </div>
            }
          />

          {/* Seperator */}
          <div className='cpf-wrapper_seperator'>
            <Separator />
          </div>

          {/* Button Group */}
          <FooterButtons onBack={handleNavigateBack} onNext={handleNavigateNext} nextDisabled={!(agree1 && agree2 && isPartBCompleted)} />
        </div>
      </div>
    </ApplicationCooLayout>
  );
}
export default StageEHomeProtectionScheme;
